/**
 * LP Sub Grid — pestañas + tarjetas de suscripción.
 * Los precios y planes llegan en el JSON de cada tarjeta (snippets/lp-sub-card.liquid);
 * aquí solo se pinta y se envía a /cart/add con `selling_plan`.
 */
if (!customElements.get('lp-sub-grid')) {
  customElements.define(
    'lp-sub-grid',
    class LpSubGrid extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
        this.tabs.forEach((tab, i) => {
          tab.addEventListener('click', () => this.select(i));
          tab.addEventListener('keydown', (e) => this.onTabKey(e, i));
        });
        this.querySelectorAll('[data-card]').forEach((card) => this.initCard(card));
      }

      select(index) {
        this.tabs.forEach((tab, i) => {
          const on = i === index;
          tab.setAttribute('aria-selected', String(on));
          tab.tabIndex = on ? 0 : -1;
          this.panels[i].hidden = !on;
        });
      }

      onTabKey(e, i) {
        const n = this.tabs.length;
        let next = null;
        if (e.key === 'ArrowRight') next = (i + 1) % n;
        else if (e.key === 'ArrowLeft') next = (i + n - 1) % n;
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = n - 1;
        if (next === null) return;
        e.preventDefault();
        this.select(next);
        this.tabs[next].focus();
      }

      initCard(card) {
        const data = JSON.parse(card.querySelector('[data-json]').textContent);
        const els = {
          price: card.querySelector('[data-price]'),
          compare: card.querySelector('[data-compare]'),
          save: card.querySelector('[data-save]'),
          unit: card.querySelector('[data-unit]'),
          cta: card.querySelector('[data-cta]'),
          ctaText: card.querySelector('[data-cta-text]'),
          error: card.querySelector('[data-error]'),
          variant: card.querySelector('[data-variant]'),
          plan: card.querySelector('[data-plan]'),
          img: card.querySelector('[data-img]'),
        };
        const state = { variantId: data.current, planId: null };
        const variantOf = (id) => data.variants.find((v) => v.id === Number(id)) || data.variants[0];

        const fillPlans = (keepId) => {
          const v = variantOf(state.variantId);
          els.plan.innerHTML = '';
          v.plans.forEach((pl) => {
            const o = document.createElement('option');
            o.value = pl.id;
            o.textContent = pl.short || pl.name;
            els.plan.appendChild(o);
          });
          const keep = v.plans.find((pl) => pl.id === Number(keepId));
          state.planId = keep ? keep.id : v.plans.length ? v.plans[0].id : null;
          if (state.planId) els.plan.value = state.planId;
        };

        const paint = () => {
          const v = variantOf(state.variantId);
          const pl = v.plans.find((x) => x.id === state.planId);
          els.error.hidden = true;
          if (!pl) {
            els.price.textContent = '';
            els.compare.textContent = '';
            els.save.hidden = true;
            els.cta.disabled = true;
            els.ctaText.textContent = els.cta.dataset.unavailable || '';
            return;
          }
          els.price.textContent = pl.priceFmt;
          if (els.unit) {
            const w = Number(data.washes) || 0;
            els.unit.hidden = !(w > 0 && data.unitLabel);
            if (w > 0 && data.unitLabel) {
              const amount = (pl.price / 100 / w).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
              els.unit.textContent = data.unitLabel.replace('{amount}', amount);
            }
          }
          const hasSave = pl.compare > pl.price;
          els.compare.textContent = hasSave ? pl.compareFmt : '';
          els.save.hidden = !hasSave;
          if (hasSave) els.save.textContent = data.saveLabel.replace('{amount}', pl.saveFmt);
          els.ctaText.textContent = data.label.replace('{price}', pl.priceFmt);
          els.cta.disabled = !v.available;
          if (els.img && v.img) els.img.src = v.img;
        };

        if (els.variant) {
          els.variant.addEventListener('change', () => {
            state.variantId = Number(els.variant.value);
            fillPlans(state.planId);
            paint();
          });
        }
        els.plan.addEventListener('change', () => {
          state.planId = Number(els.plan.value);
          paint();
        });
        els.cta.addEventListener('click', () => this.add(card, state, els));

        // Si la primera variante no tiene planes pero otra sí, arrancar en esa.
        if (!variantOf(state.variantId).plans.length) {
          const alt = data.variants.find((v) => v.available && v.plans.length);
          if (alt) {
            state.variantId = alt.id;
            if (els.variant) els.variant.value = alt.id;
          }
        }
        fillPlans(null);
        paint();
      }

      // El drawer añade `overflow-hidden` al body al abrirse y lo quita en close().
      // Si por cualquier camino el drawer queda cerrado con el bloqueo puesto, la
      // página no hace scroll: aquí se garantiza que el bloqueo siga al drawer.
      releaseScrollLock(cart) {
        if (cart._lpScrollWatch) return;
        const sync = () => {
          if (!cart.classList.contains('active')) document.body.classList.remove('overflow-hidden');
        };
        cart._lpScrollWatch = new MutationObserver(sync);
        cart._lpScrollWatch.observe(cart, { attributes: true, attributeFilter: ['class'] });
      }

      async add(card, state, els) {
        if (els.cta.getAttribute('aria-disabled') === 'true' || !state.planId) return;
        els.cta.setAttribute('aria-disabled', 'true');
        els.cta.classList.add('is-loading');
        els.error.hidden = true;

        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const fixed = document.querySelector('fixed-cart');
        const body = new FormData();
        body.append('id', state.variantId);
        body.append('quantity', 1);
        body.append('selling_plan', state.planId);
        if (cart) {
          body.append('sections', cart.getSectionsToRender().map((s) => s.id));
          body.append('sections_url', window.location.pathname);
          if (cart.setActiveElement) cart.setActiveElement(document.activeElement);
        }

        try {
          const res = await fetch(window.routes.cart_add_url, {
            method: 'POST',
            headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
            body,
          });
          const json = await res.json();
          if (json.status) {
            els.error.textContent = json.description || json.message || '';
            els.error.hidden = false;
            return;
          }
          if (!cart) {
            window.location = window.routes.cart_url;
            return;
          }
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'lp-sub-grid', productVariantId: state.variantId, cartData: json });
          }
          cart.renderContents(json);
          this.releaseScrollLock(cart);
          if (fixed && fixed.renderContents) fixed.renderContents(json);
          this.dispatchEvent(new CustomEvent('lp-sub:added', { bubbles: true, detail: { cart: json } }));
        } catch (err) {
          console.error('[lp-sub-grid]', err);
          els.error.textContent = this.dataset.errorText || '';
          els.error.hidden = !els.error.textContent;
        } finally {
          els.cta.classList.remove('is-loading');
          els.cta.removeAttribute('aria-disabled');
        }
      }
    }
  );
}
